import { type UseQueryOptions } from '@tanstack/react-query';

import type { FieldPath, Fields } from '../../../@types/field-path.d';
import { getResourceQueryKey, ResourceType, useResourceQuery } from '../../resources';
import { type Group } from '../types';

const GROUP_RESOURCE_TYPE = ResourceType.GROUPS;

export type WorkspaceQueryField = FieldPath<Group> | keyof Group;

export type WorkspaceQueryData<F extends WorkspaceQueryField = WorkspaceQueryField> = Fields<Group, F>;

export type WorkspaceQueryVariables<F extends readonly WorkspaceQueryField[]> = {
	id: string;
	fields: F;
};

export const useWorkspaceQuery = <
	const F extends readonly WorkspaceQueryField[],
	TData = WorkspaceQueryData<F[number]>,
	TError = unknown,
>(
	variables: WorkspaceQueryVariables<F>,
	options?: UseQueryOptions<WorkspaceQueryData<F[number]>, TError, TData>
) => {
	const fields = variables.fields;

	return useResourceQuery<WorkspaceQueryData<F[number]>, WorkspaceQueryData<F[number]>, TError, TData>(
		{
			resourceId: variables.id,
			_fields: fields as unknown as string[],
		},
		{
			resourceType: GROUP_RESOURCE_TYPE,
			queryKeyPrefix: 'workspace',
		},
		options
	);
};

export const getWorkspaceQueryKey = <F extends readonly WorkspaceQueryField[]>(
	variables: WorkspaceQueryVariables<F>
) => {
	// The same variables `useWorkspaceQuery` passes, or a cache write misses the entry.
	return getResourceQueryKey(
		{ resourceId: variables.id, _fields: variables.fields as unknown as string[] },
		GROUP_RESOURCE_TYPE,
		'workspace'
	);
};
